import { useEffect, useMemo, useState } from 'react';
import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import { Practitioner } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/practitioner.pb';
import { isEqual } from 'lodash-es';
import {
  ScheduleMultiSelectDropdown,
  ScheduleMultiSelectDropdownContext,
  ScheduleMultiSelectDropdownContextType,
} from '../components/ScheduleMultiSelectDropdown';

export type UseScheduleMultiSelectDropdownFieldParams = Pick<
  ScheduleMultiSelectDropdownContextType,
  'placeholder' | 'values' | 'defaultSearchValue'
> & {
  list: Practitioner[] | AppointmentType[];
  isRequired?: boolean;
};

export const useScheduleMultiSelectDropdownField = ({
  placeholder,
  values,
  defaultSearchValue,
  list,
  isRequired = false,
}: UseScheduleMultiSelectDropdownFieldParams) => {
  const id = `schedule-multi-select-dropdown-${Math.random()}`;
  const [selectedValues, setSelectedValues] = useState<string[]>(values);
  const [isError, setIsError] = useState(false);
  const [listValues, setListValues] = useState<Practitioner[] | AppointmentType[]>(list);

  const getNameFieldValue = (item: Practitioner | AppointmentType) => {
    let fullName = '';
    if ('firstName' in item || 'lastName' in item) {
      fullName = item.firstName || item.lastName ? `${item.firstName} ${item.lastName}` : '';
    }
    const name = fullName || item.externalName || item.displayName;
    return name || '';
  };

  useEffect(() => {
    if (values.length > 0 && !isEqual(values, selectedValues)) {
      setSelectedValues(values);
    }
  }, [values]);

  const providerValues = useMemo<ScheduleMultiSelectDropdownContextType>(
    () => ({
      id,
      placeholder,
      defaultSearchValue: defaultSearchValue || '',
      setIsError,
      setValues: setSelectedValues,
      values: selectedValues,
      setListValues,
      getNameFieldValue,
      initialListValues: list,
      isRequired,
      isError,
    }),
    [id, selectedValues, isError, isRequired, placeholder, defaultSearchValue, list, values, isRequired]
  );

  return {
    values: selectedValues,
    isError,
    ScheduleMultiSelectDropdownField: (
      <ScheduleMultiSelectDropdownContext.Provider value={providerValues}>
        <ScheduleMultiSelectDropdown>
          {listValues?.map((item) => (
            <ScheduleMultiSelectDropdown.Option key={item.id} value={item.id}>
              {getNameFieldValue(item)}
            </ScheduleMultiSelectDropdown.Option>
          ))}
        </ScheduleMultiSelectDropdown>
      </ScheduleMultiSelectDropdownContext.Provider>
    ),
  };
};
